import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { themeColors, satisfactionToNumeric } from '../../common/constants';
import _ from 'lodash';

import surveyApi from '../../../services/surveyApi';

const SatisfactionTrendChart = ({ loading, dateRange, isFiltered, activeFilters }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [currentData, setCurrentData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSatisfactionTrendData = async () => {
      try {
        setIsLoading(true);
        let response;
        
        if (isFiltered && activeFilters && Object.keys(activeFilters).length > 0) {
          response = await surveyApi.getSurvey(activeFilters);
        } else {
          const dateParams = getDateRangeParams(dateRange || 'last30');
          response = await surveyApi.getSurveyWithDateRange(dateParams.startDate, dateParams.endDate);
        }
        
        if (response.data && response.data.success) {
          const surveys = response.data.data.data || response.data.data;
          const processedData = processSatisfactionTrendData(surveys);
          
          // Extract available years
          const years = Object.keys(processedData)
            .map(year => parseInt(year))
            .sort((a, b) => b - a); // Sort descending
          
          setAvailableYears(years);
          
          // Set most recent year as default if it's not already selected
          // or if the selected year is not in the available years
          if (years.length > 0 && !years.includes(selectedYear)) {
            setSelectedYear(years[0]);
          }
          
          // Set the data for the selected year
          if (years.includes(selectedYear)) {
            setCurrentData(processedData[selectedYear] || []);
          } else if (years.length > 0) {
            setCurrentData(processedData[years[0]] || []);
          } else {
            setCurrentData([]);
          }
        } else {
          setError('Failed to load satisfaction trend data');
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching satisfaction trend data:', err);
        setError('Failed to load satisfaction trend data');
        setIsLoading(false);
      }
    };
    
    fetchSatisfactionTrendData();
  }, [dateRange, isFiltered, activeFilters, selectedYear]);
  
  // Process the raw survey data into monthly satisfaction trend data
  const processSatisfactionTrendData = (surveys) => {
    // Early return if surveys is not an array or is empty
    if (!Array.isArray(surveys) || surveys.length === 0) {
      return {};
    }
    
    // Group surveys by year and month
    const groupedByYearMonth = _.groupBy(surveys, (survey) => {
      const date = new Date(survey.created_at);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    });
    
    // Calculate satisfaction percentage for each month
    const monthlyData = Object.entries(groupedByYearMonth).map(([yearMonth, monthSurveys]) => {
      const [year, month] = yearMonth.split('-').map(num => parseInt(num));
      const satisfactionFields = ['sqd0', 'sqd1', 'sqd2', 'sqd3', 'sqd4', 'sqd5', 'sqd6', 'sqd7', 'sqd8', 'sqd9', 'sqd10'];
      
      let totalSatisfaction = 0;
      let fieldCount = 0;
      
      satisfactionFields.forEach(field => {
        const fieldValues = monthSurveys
          .filter(survey => survey[field] && survey[field] !== 'N/A')
          .map(survey => {
            // If the field is already a number, use it directly
            if (typeof survey[field] === 'number') {
              return survey[field];
            }
            
            // Otherwise, convert from string representation (e.g., "4 - Satisfied")
            try {
              return calculateSatisfactionValue(survey[field]);
            } catch (err) {
              console.error(`Error converting ${field} value:`, err);
              return 0;
            }
          });
        
          if (fieldValues.length > 0) {
            const fieldAvg = fieldValues.reduce((sum, val) => sum + val, 0) / fieldValues.length;
            
            // Convert to percentage (1=0%, 5=100%)
            const percentageValue = ((fieldAvg - 1) / 4) * 100;
            
            // Assign label based on percentage range
            let satisfactionLabel;
            if (percentageValue >= 0 && percentageValue < 20) {
                satisfactionLabel = "Strongly Disagree";
            } else if (percentageValue >= 20 && percentageValue < 40) {
                satisfactionLabel = "Disagree";
            } else if (percentageValue >= 40 && percentageValue < 60) {
                satisfactionLabel = "Neutral"; // Consistent with PHP function
            } else if (percentageValue >= 60 && percentageValue < 80) {
                satisfactionLabel = "Agree";
            } else if (percentageValue >= 80 && percentageValue <= 100) {
                satisfactionLabel = "Strongly Agree";
            } else {
                satisfactionLabel = "No Data";
            }
            
            // Add to total satisfaction (using the percentage value)
            totalSatisfaction += percentageValue;
            fieldCount++;
    
            return {
                percentage: percentageValue,
                label: satisfactionLabel
            };}
      });
      
      // Calculate average satisfaction percentage across all SQD fields
      const avgSatisfaction = fieldCount > 0 
        ? totalSatisfaction / fieldCount
        : 0;
      
      return {
        year,
        month: getMonthName(month - 1), 
        satisfaction: parseFloat(avgSatisfaction.toFixed(2)),
        yearMonth 
      };
    });
    
    // Sort data chronologically
    const sortedData = _.sortBy(monthlyData, 'yearMonth');
    
    // Group by year for filtering
    const dataByYear = _.groupBy(sortedData, 'year');
    
    return dataByYear;
  };
  
  // Calculate satisfaction value using the imported function
  const calculateSatisfactionValue = (satisfactionString) => {
    // Use the imported satisfactionToNumeric function
    if (typeof satisfactionToNumeric === 'function') {
      return satisfactionToNumeric(satisfactionString);
    }
    
    // Fallback implementation
    if (typeof satisfactionString === 'number') {
      return satisfactionString;
    }
    
    const match = String(satisfactionString).match(/^(\d+)/);
    if (match && match[1]) {
      return parseInt(match[1]);
    }
    
    // Default to middle value if can't parse
    return 3;
  };
  
  // Helper to get date range parameters - copied from AdminDashboard
  const getDateRangeParams = (range) => {
    const endDate = new Date();
    let startDate = new Date();
    
    switch (range) {
      case 'last7':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'last30':
        startDate.setDate(endDate.getDate() - 30);
        break;
      case 'last90':
        startDate.setDate(endDate.getDate() - 90);
        break;
      case 'thisYear':
        startDate = new Date(endDate.getFullYear(), 0, 1); // January 1st of current year
        break;
      case 'allTime':
        startDate = new Date(2000, 0, 1); // Far back enough to include all data
        break;
      default:
        startDate.setDate(endDate.getDate() - 30); // Default to last 30 days
    }
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };
  
  // Helper to format date
  const formatDate = (date) => {
    return date.toISOString().split('T')[0];
  };
  
  // Get month name from index
  const getMonthName = (monthIndex) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthIndex];
  };
  
  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  
  const showLoading = loading || isLoading;

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-4">
        {availableYears.length > 1 && (
          <div className="flex items-center">
            <label htmlFor="yearFilter" className="mr-2 text-sm font-medium text-gray-700">
              Filter by Year:
            </label>
            <select
              id="yearFilter"
              value={selectedYear}
              onChange={handleYearChange}
              className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {showLoading ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500">Loading data...</p>
        </div>
      ) : error ? (
        <div className="flex justify-center items-center h-64">
          <p className="text-red-500">{error}</p>
        </div>
      ) : currentData && currentData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={currentData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis domain={[0, 100]} />
            <Tooltip formatter={(value) => [`${value.toFixed(2)}%`, 'Satisfaction']} />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="satisfaction" 
              stroke={themeColors.primary} 
              activeDot={{ r: 8 }} 
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex justify-center items-center h-64 border border-gray-200 rounded">
          <p className="text-gray-500">No data available for the selected period</p>
        </div>
      )}
    </div>
  );
};

export default SatisfactionTrendChart;